import React from "react";
import { type GetStaticPaths, type GetStaticProps, type NextPage } from "next";
import {
  type Page as SanityPage,
  getPage,
  getBlogCategories,
  type BlogCategory,
  type BlogSimple,
  getBlogs,
  getBlogCount,
  blogsPerPageLimit,
} from "lib/sanity";
import { BlogDirectoryTemplate } from "components/blog";
import { defaultCategory } from "components/blog/blog-sidebar";

type PageProps = {
  page: SanityPage;
  allPosts: BlogSimple[];
  category: BlogCategory;
  categoryBlogCount: number;
  categories: BlogCategory[];
  currentPage: number;
};

const BlogDirectoryPage: NextPage<Readonly<PageProps>> = ({
  page,
  allPosts,
  category,
  categories,
  categoryBlogCount,
  currentPage,
}) => {
  return (
    <BlogDirectoryTemplate
      metaTitle={page.metaTitle}
      metaDescription={page.metaDescription}
      metaImageUrl={page.metaImageUrl}
      allPosts={allPosts}
      initialCategory={category}
      initialCategoryBlogCount={categoryBlogCount}
      initialPage={currentPage}
      categories={categories}
    />
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const categories = await getBlogCategories();
  categories.push(defaultCategory);

  const categoryCountPromises = categories.map(async (category) =>
    getBlogCount(category.slug).then((count) => ({
      slug: category.slug,
      totalPages: Math.ceil(count / blogsPerPageLimit),
    })),
  );

  const categoryPages = await Promise.all(categoryCountPromises);

  const paths = categoryPages.flatMap(({ slug, totalPages }) =>
    Array.from({ length: totalPages }, (_, i) => ({
      params: {
        slug,
        page: (i + 1).toString(),
      },
    })),
  );

  return {
    paths,
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps<PageProps> = async ({ params }) => {
  const categorySlug = params?.slug as string;
  const pageNumber = params?.page ? Number(params.page) : 1;

  const [page, categories] = await Promise.all([getPage("/blog"), getBlogCategories()]);

  const category = categorySlug === "all" ? defaultCategory : categories.find((c) => c.slug === categorySlug);

  if (!page) {
    return { notFound: true };
  }

  if (!category) {
    return {
      redirect: {
        destination: "/blog/category/all/page/1",
        permanent: false,
      },
    };
  }

  const [allPosts, categoryBlogCount] = await Promise.all([
    getBlogs(category.slug, pageNumber),
    getBlogCount(category.slug),
  ]);

  const categoryPageCount = Math.ceil(categoryBlogCount / blogsPerPageLimit);

  if (pageNumber < 1 || pageNumber > categoryPageCount) {
    return {
      redirect: {
        destination: `/blog/category/${category.slug}/page/1`,
        permanent: false,
      },
    };
  }

  return {
    props: {
      page,
      categories,
      category,
      allPosts,
      categoryBlogCount,
      currentPage: pageNumber,
    },
    revalidate: 60,
  };
};

export default BlogDirectoryPage;
